/**
 * Hand-rolled SVG chart primitives for the PDF report document.
 *
 * Deliberately not recharts (which is a dependency of swa-axn-customer, for
 * on-screen use) and not a Tailwind chart kit. Three reasons specific to this
 * renderer:
 *
 *  - pdf-report.css is mm/pt based with fixed 210x296mm pages. Library output is
 *    px-based and fights that.
 *  - The renderer is fail-closed on `window.__PRINT_READY__` and does per-page
 *    overflow checks. Anything that defers layout behind a ResizeObserver, or
 *    animates in, races that check. Everything here is a pure function of its
 *    props: no state, no effects, no animation, no measurement.
 *  - The PDF artifact hash folds in templateId + templateVersion but not a chart
 *    library version, so a dependency bump would silently change the pixels of a
 *    nominally immutable report.
 *
 * All dimensions are in mm to match the page geometry. Colours come from the
 * template theme via CSS custom properties already on `.pdf-document`.
 */

interface Sized {
  /** Width in mm. Callers pass the column width they have. */
  widthMm: number;
  heightMm: number;
}

const mm = (n: number) => `${n}mm`;

function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
}

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

export function PdfStatTile({
  value,
  label,
  sub,
  accent = 'var(--pdf-blue)',
}: {
  value: string;
  label: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="pdf-stat-tile">
      <div className="pdf-stat-tile__value" style={{ color: accent }}>
        {value}
      </div>
      <div className="pdf-stat-tile__label">{label}</div>
      {sub && <div className="pdf-stat-tile__sub">{sub}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend line
// ---------------------------------------------------------------------------

export interface TrendPoint {
  date: string;
  value: number;
}

/**
 * A single series over time. Y axis is deliberately NOT zero-based when the
 * series is tightly clustered — a secure score moving 955→960 out of 1290 is a
 * flat line on a zero-based axis, which hides the story. The axis labels always
 * state the real bounds so the zoom is never implicit.
 */
/**
 * `percent` is not cosmetic — it changes what the chart claims.
 *
 * Fitting the y-band to the data is right for an open-ended series, and wrong
 * for a percentage that barely moves: Microsoft Secure Score wanders under a
 * point a month, and an auto-fitted axis turns that noise into a hockey stick
 * an executive reads as a dramatic climb. On a page whose whole brief is
 * "somebody reads a visual and nobody explains it to them", that is the most
 * expensive mistake available. So a percentage series gets a floor of ten
 * points of range, snapped to whole numbers and clamped to 0-100: a real move
 * still shows, and noise reads flat, which is what it is.
 */
export function PdfTrendLine({
  points,
  widthMm,
  heightMm,
  stroke = 'var(--pdf-blue)',
  valueSuffix = '',
  percent = false,
}: Sized & {
  points: TrendPoint[];
  stroke?: string;
  valueSuffix?: string;
  percent?: boolean;
}) {
  if (points.length === 0) return null;

  const MIN_PERCENT_SPAN = 10;

  const padL = 14;
  const padR = 2;
  const padT = 3;
  const padB = 7;

  const values = points.map((p) => p.value);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const span = rawMax - rawMin;
  // Pad the band so the line never sits on the frame; guarantee a non-zero span
  // for a perfectly flat series.
  const pad = span === 0 ? Math.max(1, Math.abs(rawMax) * 0.02) : span * 0.15;
  let min = rawMin - pad;
  let max = rawMax + pad;

  if (percent) {
    if (max - min < MIN_PERCENT_SPAN) {
      const mid = (rawMin + rawMax) / 2;
      min = mid - MIN_PERCENT_SPAN / 2;
      max = mid + MIN_PERCENT_SPAN / 2;
    }
    // Whole-number ticks, then slide (never squeeze) the band back inside
    // 0-100 so an axis never labels an impossible percentage.
    min = Math.floor(min);
    max = Math.ceil(max);
    if (max > 100) {
      min -= max - 100;
      max = 100;
    }
    if (min < 0) {
      max = Math.min(100, max - min);
      min = 0;
    }
  }

  const plotW = widthMm - padL - padR;
  const plotH = heightMm - padT - padB;

  const x = (i: number) =>
    points.length === 1 ? padL + plotW / 2 : padL + (i / (points.length - 1)) * plotW;
  const y = (v: number) => padT + plotH - ((v - min) / (max - min)) * plotH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(p.value).toFixed(2)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(2)},${(padT + plotH).toFixed(2)} L${x(0).toFixed(2)},${(padT + plotH).toFixed(2)} Z`;

  const fmt = (v: number) =>
    `${percent ? Math.round(v) : Math.round(v * 10) / 10}${valueSuffix}`;
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <svg
      width={mm(widthMm)}
      height={mm(heightMm)}
      viewBox={`0 0 ${widthMm} ${heightMm}`}
      className="pdf-chart"
      role="img"
      aria-label={`Trend from ${fmt(first.value)} to ${fmt(last.value)}`}
    >
      <line x1={padL} y1={padT} x2={widthMm - padR} y2={padT} className="pdf-chart__grid" />
      <line
        x1={padL}
        y1={padT + plotH}
        x2={widthMm - padR}
        y2={padT + plotH}
        className="pdf-chart__axis"
      />
      <path d={area} fill={stroke} opacity={0.08} />
      <path d={line} fill="none" stroke={stroke} strokeWidth={0.6} strokeLinejoin="round" />
      <circle cx={x(points.length - 1)} cy={y(last.value)} r={0.9} fill={stroke} />

      <text x={padL - 1.5} y={padT + 1.2} textAnchor="end" className="pdf-chart__tick">
        {fmt(max)}
      </text>
      <text x={padL - 1.5} y={padT + plotH} textAnchor="end" className="pdf-chart__tick">
        {fmt(min)}
      </text>
      <text x={padL} y={heightMm - 1.5} className="pdf-chart__tick">
        {first.date}
      </text>
      <text x={widthMm - padR} y={heightMm - 1.5} textAnchor="end" className="pdf-chart__tick">
        {last.date}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Donut
// ---------------------------------------------------------------------------

/** A single proportion. Used for coverage percentages, not for part-to-whole
 * breakdowns of many categories — a donut with six slices is unreadable at
 * this size, which is what PdfBarList is for. */
export function PdfDonut({
  percent,
  label,
  sizeMm,
  stroke = 'var(--pdf-blue)',
  track = '#e4e6ec',
}: {
  percent: number;
  label?: string;
  sizeMm: number;
  stroke?: string;
  track?: string;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const r = sizeMm / 2 - 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;

  return (
    <svg
      width={mm(sizeMm)}
      height={mm(sizeMm)}
      viewBox={`0 0 ${sizeMm} ${sizeMm}`}
      className="pdf-chart"
      role="img"
      aria-label={`${clamped}%${label ? ` ${label}` : ''}`}
    >
      <circle cx={sizeMm / 2} cy={sizeMm / 2} r={r} fill="none" stroke={track} strokeWidth={2.6} />
      <circle
        cx={sizeMm / 2}
        cy={sizeMm / 2}
        r={r}
        fill="none"
        stroke={stroke}
        strokeWidth={2.6}
        strokeDasharray={`${dash.toFixed(2)} ${(c - dash).toFixed(2)}`}
        strokeLinecap="butt"
        transform={`rotate(-90 ${sizeMm / 2} ${sizeMm / 2})`}
      />
      <text
        x={sizeMm / 2}
        y={sizeMm / 2 + (label ? 0 : 1.4)}
        textAnchor="middle"
        className="pdf-chart__donut-value"
      >
        {Math.round(clamped)}%
      </text>
      {label && (
        <text x={sizeMm / 2} y={sizeMm / 2 + 4} textAnchor="middle" className="pdf-chart__donut-label">
          {label}
        </text>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bar list
// ---------------------------------------------------------------------------

export interface BarDatum {
  label: string;
  value: number;
  /** Optional per-bar colour; defaults to the theme blue. */
  color?: string;
}

/**
 * Ranked horizontal bars. Horizontal rather than vertical because the labels
 * are text of unpredictable length ("Moved to quarantine"), and rotated axis
 * labels are the first thing to break a fixed-height print layout.
 */
export function PdfBarList({
  data,
  widthMm,
  rowHeightMm = 6,
  labelWidthMm = 42,
  valueSuffix = '',
}: {
  data: BarDatum[];
  widthMm: number;
  rowHeightMm?: number;
  labelWidthMm?: number;
  valueSuffix?: string;
}) {
  if (data.length === 0) return null;

  const max = niceCeil(Math.max(...data.map((d) => d.value)));
  const valueWidthMm = 14;
  const trackW = widthMm - labelWidthMm - valueWidthMm;
  const heightMm = data.length * rowHeightMm;

  return (
    <svg
      width={mm(widthMm)}
      height={mm(heightMm)}
      viewBox={`0 0 ${widthMm} ${heightMm}`}
      className="pdf-chart"
      role="img"
      aria-label={data.map((d) => `${d.label}: ${d.value}`).join('; ')}
    >
      {data.map((d, i) => {
        const y = i * rowHeightMm;
        const barH = rowHeightMm * 0.52;
        const w = max === 0 ? 0 : (d.value / max) * trackW;
        return (
          <g key={`${d.label}-${i}`}>
            <text x={0} y={y + rowHeightMm / 2 + 1} className="pdf-chart__bar-label">
              {d.label}
            </text>
            <rect
              x={labelWidthMm}
              y={y + (rowHeightMm - barH) / 2}
              width={trackW}
              height={barH}
              fill="#eef0f4"
              rx={0.4}
            />
            <rect
              x={labelWidthMm}
              y={y + (rowHeightMm - barH) / 2}
              width={Math.max(w, d.value > 0 ? 0.6 : 0)}
              height={barH}
              fill={d.color ?? 'var(--pdf-blue)'}
              rx={0.4}
            />
            <text
              x={widthMm}
              y={y + rowHeightMm / 2 + 1}
              textAnchor="end"
              className="pdf-chart__bar-value"
            >
              {d.value.toLocaleString()}
              {valueSuffix}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Stacked bar (single row)
// ---------------------------------------------------------------------------

export interface StackSegment {
  label: string;
  value: number;
  color: string;
}

/**
 * One horizontal bar split into proportional segments, with an inline legend.
 *
 * For a severity mix, where the shape of the distribution is the message and the
 * individual counts are secondary — a four-bar chart would give equal visual
 * weight to categories that differ by two orders of magnitude.
 *
 * Segments below a hairline are floored to a visible width so a category with a
 * real count never renders as nothing; the legend carries the true numbers.
 */
export function PdfStackedBar({
  segments,
  widthMm,
  heightMm = 5,
}: {
  segments: StackSegment[];
  widthMm: number;
  heightMm?: number;
}) {
  const present = segments.filter((s) => s.value > 0);
  const total = present.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return null;

  const MIN = 0.8;
  let x = 0;

  return (
    <div>
      <svg
        width={mm(widthMm)}
        height={mm(heightMm)}
        viewBox={`0 0 ${widthMm} ${heightMm}`}
        className="pdf-chart"
        role="img"
        aria-label={present.map((s) => `${s.label}: ${s.value}`).join('; ')}
      >
        {present.map((s) => {
          const raw = (s.value / total) * widthMm;
          const w = Math.max(raw, MIN);
          const seg = (
            <rect key={s.label} x={x} y={0} width={w} height={heightMm} fill={s.color} />
          );
          x += w;
          return seg;
        })}
      </svg>
      <div className="pdf-stack-legend">
        {present.map((s) => (
          <span key={s.label}>
            <i style={{ background: s.color }} />
            {s.label} {s.value.toLocaleString()}
          </span>
        ))}
      </div>
    </div>
  );
}
